/**
 * Mon espace — le back-office de l'entrepreneur.
 *
 * Même coquille que l'espace organisateur (barre latérale, cartes charbon,
 * dégradé réservé) : c'est le même produit, vu depuis l'autre côté.
 *
 * Six écrans, dans l'ordre d'une journée :
 *   · Fil               — ce qui a bougé depuis hier, ce qui attend une réponse ;
 *   · Moi et mon entreprise — la matière qui fait tout le reste ;
 *   · Influence         — ce que je pèse, et ce qui le ferait monter ;
 *   · Mises en relation — présenter, être présenté, décider ;
 *   · Événements        — où aller, et pourquoi ;
 *   · Rendez-vous       — ma page de prise de rendez-vous, mon agenda, et qui veut me voir ;
 *   · Pré-enregistrement — l'abonnement : voir avant, ne rien rater, être vu ;
 *   · Partenaires       — l'espace sponsoring.
 */
import { useCallback, useEffect, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { LogOut, Menu, Shield, X } from 'lucide-react';
import { appelerApi, envoyer } from '../reseau.ts';
import { Marque } from '../composants/Marque.tsx';
import { CompteConnexion } from './CompteCreer.tsx';
import { Fil } from './compte/Fil.tsx';
import { Influence } from './compte/Influence.tsx';
import { MisesEnRelation } from './compte/MisesEnRelation.tsx';
import { MonEntreprise } from './compte/MonEntreprise.tsx';
import { Evenements } from './compte/Evenements.tsx';
import { RendezVous } from './compte/Rencontres.tsx';
import { PreEnregistrement } from './compte/PreEnregistrement.tsx';
import { Partenaires } from './compte/Partenaires.tsx';
import { Photo } from './compte/Photo.tsx';
import { Carnet } from './compte/Carnet.tsx';
import { Relance } from './compte/Relance.tsx';
import { nomDe, type Espace } from './compte/types.ts';

export function EspaceCompte() {
  const [espace, setEspace] = useState<Espace | null | undefined>(undefined);
  const [pistes, setPistes] = useState(0);
  const [ouvert, setOuvert] = useState(false);

  const charger = useCallback(async () => {
    const r = await appelerApi<Espace>('/api/compte/moi');
    setEspace(r.donnees ?? null);
    if (r.donnees) {
      const p = await appelerApi<{ n: number }>('/api/mise-en-relation/en-attente');
      setPistes(p.donnees?.n ?? 0);
    }
  }, []);

  useEffect(() => {
    void charger();
  }, [charger]);

  if (espace === undefined) return <p className="min-h-screen bg-void p-10 text-fumee">Chargement…</p>;
  if (espace === null) return <CompteConnexion />;
  if (espace.doit_redefinir) return <RedefinirMotDePasse email={espace.identite.email} onFait={charger} />;

  return (
    <div className="min-h-screen bg-void font-sans text-blanc">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-[248px] overflow-y-auto border-r border-graphite lg:block">
        <BarreLaterale espace={espace} pistes={pistes} />
      </aside>

      <div className="flex items-center justify-between border-b border-graphite px-4 py-3 lg:hidden">
        <span className="text-[15px] font-medium">Mon espace</span>
        <button onClick={() => setOuvert(true)} aria-label="Ouvrir la navigation" className="text-fumee hover:text-blanc">
          <Menu size={20} strokeWidth={1.5} />
        </button>
      </div>

      {ouvert && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-void/80" onClick={() => setOuvert(false)} />
          <div className="absolute inset-y-0 left-0 w-[272px] overflow-y-auto border-r border-graphite bg-void">
            <button onClick={() => setOuvert(false)} aria-label="Fermer la navigation" className="absolute right-3 top-4 z-10 text-fumee hover:text-blanc">
              <X size={18} strokeWidth={1.5} />
            </button>
            <BarreLaterale espace={espace} pistes={pistes} onNavigate={() => setOuvert(false)} />
          </div>
        </div>
      )}

      <main className="px-4 pb-24 pt-8 sm:px-8 lg:ml-[248px] lg:px-10 lg:py-12">
        <div className="mx-auto max-w-[1080px]">
          {/* Chemins RELATIFS : ce bloc est monté sous `/compte/*`, le préfixe
              est déjà consommé par la route parente (App.tsx). */}
          <Routes>
            <Route index element={<Fil espace={espace} onChange={charger} />} />
            <Route path="entreprise" element={<MonEntreprise espace={espace} onChange={charger} />} />
            <Route path="photo" element={<Photo espace={espace} onChange={charger} />} />
            <Route path="influence" element={<Influence />} />
            <Route path="mises-en-relation" element={<MisesEnRelation />} />
            <Route path="evenements" element={<Evenements espace={espace} onChange={charger} />} />
            <Route path="rendez-vous" element={<RendezVous espace={espace} onChange={charger} />} />
            <Route path="carnet" element={<Carnet />} />
            <Route path="relance/:evenementId" element={<Relance />} />
            <Route path="rencontres" element={<Navigate to="/compte/rendez-vous" replace />} />
            <Route path="preenregistrement" element={<PreEnregistrement />} />
            <Route path="partenaires" element={<Partenaires />} />
            <Route path="*" element={<Navigate to="/compte" replace />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

/** Mot de passe posé par la plateforme : on en choisit un autre avant d'entrer. */
function RedefinirMotDePasse({ email, onFait }: { email: string | null; onFait: () => Promise<void> }) {
  const [nouveau, setNouveau] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const valider = async () => {
    if (nouveau.length < 8) return setErreur('Au moins 8 caractères.');
    if (nouveau !== confirmation) return setErreur('Les deux saisies ne correspondent pas.');
    setEnCours(true);
    setErreur(null);
    const r = await envoyer('/api/compte/mot-de-passe', { nouveau });
    setEnCours(false);
    if (r.erreur) return setErreur(r.erreur);
    await onFait();
  };
  return (
    <div className="flex min-h-screen items-center justify-center bg-void px-4 font-sans text-blanc">
      <div className="w-full max-w-[420px]">
        <div className="flex items-center gap-2.5">
          <Marque taille={20} />
          <span className="text-[15px] font-medium">EventMatch<span className="text-fumee">.pro</span></span>
        </div>
        <h1 className="display-title mt-8 text-[30px]">
          Choisissez votre <em className="text-match-gradient font-serif italic font-medium">mot de passe</em>
        </h1>
        <p className="mt-3 text-[14px] leading-relaxed text-fumee">
          Le mot de passe provisoire{email ? ` de ${email}` : ''} a été posé par la plateforme : définissez le
          vôtre avant d’entrer. Il ne sera connu que de vous.
        </p>
        <div className="mt-6 flex flex-col gap-4">
          <label className="block">
            <span className="label-caps block text-fumee">Nouveau mot de passe</span>
            <input type="password" value={nouveau} onChange={(e) => setNouveau(e.target.value)} autoComplete="new-password" className="field-input mt-2" autoFocus />
          </label>
          <label className="block">
            <span className="label-caps block text-fumee">Confirmez-le</span>
            <input type="password" value={confirmation} onChange={(e) => setConfirmation(e.target.value)} autoComplete="new-password" className="field-input mt-2" />
          </label>
          {erreur && <p className="text-[13px]" role="alert" style={{ color: 'var(--em-erreur)' }}>{erreur}</p>}
          <button onClick={() => void valider()} className="btn-creme mt-2 self-start" disabled={enCours}>
            {enCours ? '…' : 'Enregistrer et entrer'}
          </button>
        </div>
      </div>
    </div>
  );
}

function BarreLaterale({ espace, pistes, onNavigate }: { espace: Espace; pistes: number; onNavigate?: () => void }) {
  const naviguer = useNavigate();
  const initiales = nomDe(espace.identite)
    .split(/\s+/)
    .map((m) => m[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const nav = [
    { to: '/compte', label: 'Fil', exact: true, pastille: 0 },
    { to: '/compte/entreprise', label: 'Moi et mon entreprise', pastille: espace.profil?.pitch_brouillon ? 1 : 0 },
    { to: '/compte/influence', label: 'Influence', pastille: 0 },
    { to: '/compte/mises-en-relation', label: 'Mises en relation', pastille: pistes },
    { to: '/compte/evenements', label: 'Événements', pastille: 0 },
    { to: '/compte/rendez-vous', label: 'Rendez-vous', pastille: espace.demandes_en_attente },
    { to: '/compte/carnet', label: 'Mon carnet', pastille: 0 },
    { to: '/compte/preenregistrement', label: 'Pré-enregistrement', pastille: 0 },
    { to: '/compte/partenaires', label: 'Partenaires', pastille: 0 },
  ];

  return (
    <div className="flex h-full flex-col justify-between bg-void px-5 py-6" onClick={onNavigate}>
      <div>
        <a href="/" className="flex items-center gap-2.5">
          <Marque taille={20} />
          <span className="text-[15px] font-medium text-blanc">
            EventMatch<span className="text-fumee">.pro</span>
          </span>
        </a>

        <nav className="mt-9">
          <p className="label-caps mb-3 pl-4 text-fumee/70">Entrepreneur</p>
          <div className="flex flex-col gap-0.5">
            {nav.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={'exact' in l && l.exact}
                className="group relative flex items-center justify-between gap-2 py-2 pl-4 pr-2 text-[14px] text-fumee transition-colors duration-200 hover:text-blanc"
              >
                {({ isActive }) => (
                  <>
                    {isActive && <span className="bg-match-gradient absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-full" />}
                    <span className={isActive ? 'text-blanc' : ''}>{l.label}</span>
                    {l.pastille > 0 && (
                      <span className="bg-match-gradient flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[11px] font-medium text-void">
                        {l.pastille}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </div>
        </nav>
      </div>

      <div className="mt-8 border-t border-graphite pt-5">
        {espace.admin && (
          <a href="/orga" className="mb-4 flex items-center gap-2 text-[13px] text-fumee transition-colors duration-200 hover:text-blanc">
            <Shield size={14} strokeWidth={1.5} />
            Administration
          </a>
        )}
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-full border border-graphite text-[12px] text-blanc">
            {initiales || '·'}
          </span>
          <div className="min-w-0">
            <p className="truncate text-[14px] text-blanc">{nomDe(espace.identite)}</p>
            <p className="truncate text-[12px] text-fumee">{espace.profil?.entreprise ?? espace.identite.email}</p>
          </div>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            void envoyer('/api/compte/sortir', {}).then(() => naviguer('/', { replace: true }));
          }}
          className="mt-4 flex items-center gap-2 text-[13px] text-fumee transition-colors duration-200 hover:text-blanc"
        >
          <LogOut size={14} strokeWidth={1.5} />
          Se déconnecter
        </button>
      </div>
    </div>
  );
}
